import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { contentApi } from '@/lib/api';
import { useConsent } from '@/lib/consent-store';
import { useLocalePath } from '@/hooks/useLocalePath';
import type { JSX } from 'react';

const POLICY_VERSION = '2026-01';

/**
 * Defaults to rejected and genuinely blocks non-essential cookies: nothing beyond the cart
 * and this consent record is written before a choice is made, and `analytics`/`marketing`
 * start false rather than pre-ticked.
 */
export function CookieBanner(): JSX.Element | null {
  const { t } = useTranslation();
  const path = useLocalePath();
  const decidedAt = useConsent((s) => s.decidedAt);
  const decide = useConsent((s) => s.decide);
  const [expanded, setExpanded] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  if (decidedAt) return null;

  const record = (choice: { analytics: boolean; marketing: boolean }): void => {
    decide(choice, POLICY_VERSION);
    void contentApi.consent({ necessary: true, ...choice }).catch(() => undefined);
  };

  return (
    <AnimatePresence>
      <motion.aside
        initial={{ y: 80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 80, opacity: 0 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        role="dialog"
        aria-label={t('cookies.title')}
        data-testid="cookie-banner"
        /* Topmost layer. Consent must be reachable whatever else is on screen — the cart
           drawer, the filter sheet and the sticky buy bar all sit below it. A consent
           control another element can cover is a consent control that cannot be given. */
        className="fixed inset-x-0 bottom-0 z-[80] flex max-h-[85vh] flex-col bg-[var(--color-ink)] text-[var(--color-ground)]"
      >
        <div className="rz-shell flex min-h-0 flex-col gap-4 overflow-y-auto py-5">
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div className="max-w-2xl space-y-1.5">
              <p className="font-display text-sm font-semibold">{t('cookies.title')}</p>
              <p className="text-xs text-[var(--color-muted-soft)] leading-relaxed">
                {t('cookies.body')}{' '}
                <Link to={path('/strona/polityka-prywatnosci')} className="underline underline-offset-2">
                  {t('cookies.policyLink')}
                </Link>
                {' · '}
                <button type="button" onClick={() => setExpanded((v) => !v)} className="underline underline-offset-2">
                  {t('cookies.settings')}
                </button>
              </p>
            </div>
            {/* Stacked below `sm`: side by side, the Polish labels overflow a 360px screen
                and the actions end up half off the edge — consent becomes untappable. */}
            <div className="flex shrink-0 flex-col gap-2 sm:flex-row sm:items-center">
              <button
                type="button"
                onClick={() => record({ analytics: false, marketing: false })}
                data-testid="reject-cookies"
                className="rz-button rz-button--ghost w-full justify-center !text-[var(--color-ground)] !shadow-[inset_0_0_0_1px_rgba(237,233,225,0.3)] !py-3 !text-xs sm:w-auto"
              >
                {t('cookies.rejectAll')}
              </button>
              <button
                type="button"
                onClick={() => record({ analytics: true, marketing: true })}
                data-testid="accept-cookies"
                className="rz-button w-full justify-center !bg-[var(--color-ground)] !text-[var(--color-ink)] !py-3 !text-xs sm:w-auto"
              >
                {t('cookies.acceptAll')}
              </button>
            </div>
          </div>

          {expanded && (
            <div className="space-y-2 border-t border-[rgba(237,233,225,0.15)] pt-4">
              <label className="flex items-center gap-2 text-xs opacity-60">
                <input type="checkbox" checked disabled />
                {t('cookies.necessary')}
              </label>
              <label className="flex items-center gap-2 text-xs">
                <input type="checkbox" checked={analytics} onChange={(e) => setAnalytics(e.target.checked)} />
                {t('cookies.analytics')}
              </label>
              <label className="flex items-center gap-2 text-xs">
                <input type="checkbox" checked={marketing} onChange={(e) => setMarketing(e.target.checked)} />
                {t('cookies.marketing')}
              </label>
              <button type="button" onClick={() => record({ analytics, marketing })} className="rz-button !bg-[var(--color-ground)] !text-[var(--color-ink)] !px-4 !py-2.5 !text-xs">
                {t('cookies.savePreferences')}
              </button>
            </div>
          )}
        </div>
      </motion.aside>
    </AnimatePresence>
  );
}
